import { useState, useEffect, useCallback, useRef, useMemo, createContext, useContext } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, Download, Loader2, Sparkles, Check, Pencil, Play } from "lucide-react";
import { getImageUrl, getThumbnailUrl, formatFileSize } from "@/lib/utils";

/**
 * Detect a video clip by file extension (ignoring any query string). Used so
 * mixed galleries (stills + a returned `.mp4`) render each item correctly.
 */
function isVideoUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  const clean = url.split("?")[0].toLowerCase();
  return /\.(mp4|mov|webm|m4v)$/.test(clean);
}

interface LightboxImage {
  url: string;
  alt?: string;
  /**
   * When true the item is a video clip and is rendered with a `<video>`
   * element (playback controls) instead of an `<img>`. Callers may omit this
   * and rely on URL-extension detection.
   */
  isVideo?: boolean;
  isOutfit?: boolean;
  onSaveToOutfit?: () => Promise<void> | void;
  /**
   * When provided, an Edit (pencil) button appears in the lightbox's top-right
   * toolbar. The callback typically navigates to the Edit / Create Image page
   * with the current URL pre-filled (matching ModelImageRow's hover-pencil
   * behaviour). Lightbox closes itself before invoking the callback so the
   * caller can navigate without the overlay still on top.
   */
  onEdit?: () => void;
}

interface LightboxState {
  images: LightboxImage[];
  currentIndex: number;
}

interface LightboxContextType {
  open: (images: LightboxImage[], startIndex?: number) => void;
  openSingle: (url: string, alt?: string) => void;
  isOpen: boolean;
}

const LightboxContext = createContext<LightboxContextType | null>(null);

export function useLightbox() {
  const ctx = useContext(LightboxContext);
  if (!ctx) throw new Error("useLightbox must be used within LightboxProvider");
  return ctx;
}

export function LightboxProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<LightboxState | null>(null);

  const open = useCallback((images: LightboxImage[], startIndex = 0) => {
    setState({ images, currentIndex: startIndex });
  }, []);

  const openSingle = useCallback((url: string, alt?: string) => {
    setState({ images: [{ url, alt }], currentIndex: 0 });
  }, []);

  const close = useCallback(() => setState(null), []);

  const navigate = useCallback((index: number) => {
    setState((prev) => prev ? { ...prev, currentIndex: index } : null);
  }, []);

  return (
    <LightboxContext.Provider value={{ open, openSingle, isOpen: !!state }}>
      {children}
      {state && createPortal(
        <LightboxOverlay
          images={state.images}
          currentIndex={state.currentIndex}
          onClose={close}
          onNavigate={navigate}
        />,
        document.body
      )}
    </LightboxContext.Provider>
  );
}

function LightboxOverlay({
  images,
  currentIndex,
  onClose,
  onNavigate,
}: {
  images: LightboxImage[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}) {
  const current = images[currentIndex];
  const [imageStage, setImageStage] = useState<"thumb" | "medium" | "full">("thumb");

  useEffect(() => {
    setImageStage("thumb");
    if (!current) return;
    let cancelled = false;

    const loadFull = () => {
      const fullImg = new Image();
      fullImg.src = getImageUrl(current.url);
      fullImg.onload = () => { if (!cancelled) setImageStage("full"); };
      fullImg.onerror = () => { if (!cancelled) setImageStage("full"); };
    };

    const medImg = new Image();
    medImg.src = getThumbnailUrl(current.url, 600);
    medImg.onload = () => {
      if (cancelled) return;
      setImageStage("medium");
      loadFull();
    };
    medImg.onerror = () => {
      if (cancelled) return;
      loadFull();
    };

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- progressive image preload; intentionally re-runs only on image change
  }, [currentIndex, current?.url]);

  const previewSrc = useMemo(() => {
    if (!current) return "";
    if (imageStage === "full") return getImageUrl(current.url);
    if (imageStage === "medium") return getThumbnailUrl(current.url, 600);
    return getThumbnailUrl(current.url, 300);
  }, [current, imageStage]);

  useEffect(() => {
    [-1, 1].forEach((offset) => {
      const adj = images[currentIndex + offset];
      if (adj) {
        const img = new Image();
        img.src = getThumbnailUrl(adj.url, 600);
      }
    });
  }, [currentIndex, images]);

  // Hooks below previously sat AFTER the `if (!current) return null` early
  // return, which made them conditional and would crash React if the
  // component ever re-rendered with a missing image. Move all hooks above
  // the guard so the call order is stable, and use safe access (current?.x)
  // inside callbacks. hasPrev/hasNext only depend on props.
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < images.length - 1;

  const [imageMeta, setImageMeta] = useState<{ w: number; h: number; size: number | null; type: string | null } | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    setImageMeta(null);
    const controller = new AbortController();
    const url = getImageUrl(images[currentIndex]?.url || "");
    fetch(url, { method: "HEAD", signal: controller.signal })
      .then((r) => {
        const cl = r.headers.get("content-length");
        const ct = r.headers.get("content-type");
        setImageMeta((prev) => ({
          w: prev?.w || 0,
          h: prev?.h || 0,
          size: cl ? Number(cl) : null,
          type: ct ? ct.replace("image/", "").toUpperCase() : null,
        }));
      })
      .catch(() => {});
    return () => controller.abort();
  }, [currentIndex, images]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasPrev) onNavigate(currentIndex - 1);
      if (e.key === "ArrowRight" && hasNext) onNavigate(currentIndex + 1);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, onNavigate, currentIndex, hasPrev, hasNext]);

  const [savingOutfit, setSavingOutfit] = useState(false);
  const [savedOutfit, setSavedOutfit] = useState(false);
  useEffect(() => { setSavedOutfit(false); }, [currentIndex, current?.url]);

  if (!current) return null;

  const resolvedUrl = getImageUrl(current.url);
  const currentIsVideo = current.isVideo === true || isVideoUrl(current.url);

  const handleImageLoad = () => {
    if (imgRef.current) {
      const { naturalWidth: w, naturalHeight: h } = imgRef.current;
      setImageMeta((prev) => ({ w, h, size: prev?.size ?? null, type: prev?.type ?? null }));
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(resolvedUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      const t = blob.type;
      let ext = "jpg";
      if (t.includes("png")) ext = "png";
      else if (t.includes("webp")) ext = "webp";
      else if (t.includes("mp4")) ext = "mp4";
      else if (t.includes("webm")) ext = "webm";
      else if (t.includes("quicktime")) ext = "mov";
      else if (currentIsVideo) ext = "mp4";
      a.download = `${currentIsVideo ? "video" : "image"}-${Date.now()}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(resolvedUrl, "_blank");
    }
  };

  const canSaveOutfit = !!current.onSaveToOutfit;
  const isAlreadyOutfit = current.isOutfit === true || savedOutfit;
  const handleSaveOutfit = async () => {
    if (!current.onSaveToOutfit || savingOutfit || isAlreadyOutfit) return;
    setSavingOutfit(true);
    try {
      await current.onSaveToOutfit();
      setSavedOutfit(true);
    } catch {
      // Caller is responsible for surfacing the error to the user (toast).
    } finally {
      setSavingOutfit(false);
    }
  };

  return (
    <div data-image-lightbox className="fixed inset-0 z-[100] flex items-center justify-center" style={{ pointerEvents: "auto" }} onPointerDown={(e) => e.stopPropagation()}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      {/* Outer is a fixed-height column so the image area can flex/shrink and
          leave room for the thumbnail strip + action row at the bottom. The
          previous layout let the image consume 80vh on its own, which pushed
          the thumbnail strip behind/under the Download/Close buttons. */}
      <div className="relative z-10 flex flex-col items-center max-w-[90vw] h-[90vh] w-full">
        <div className="absolute top-4 right-4 z-20 flex gap-2">
          {current.onEdit && (
            <button
              className="p-2 rounded-full bg-white/20 text-white hover:bg-white/30 transition-all"
              onClick={() => {
                const cb = current.onEdit;
                onClose();
                cb?.();
              }}
              title="Edit this image in the image editor"
              data-testid="lightbox-edit-image"
            >
              <Pencil size={20} />
            </button>
          )}
          <button
            className="p-2 rounded-full bg-white/20 text-white hover:bg-white/30 transition-all"
            onClick={handleDownload}
            title="Download image"
          >
            <Download size={20} />
          </button>
          <button
            className="p-2 rounded-full bg-white/20 text-white hover:bg-white/30 transition-all"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>

        <div className="relative flex items-center gap-4 flex-1 min-h-0 w-full justify-center">
          {images.length > 1 && (
            <button
              className={`p-3 rounded-full bg-white/20 text-white transition-all flex-shrink-0 ${hasPrev ? "hover:bg-white/30" : "opacity-30 cursor-not-allowed"}`}
              onClick={(e) => { e.stopPropagation(); if (hasPrev) onNavigate(currentIndex - 1); }}
              disabled={!hasPrev}
            >
              <ChevronLeft size={24} />
            </button>
          )}

          <div className="relative h-full flex items-center justify-center min-h-0">
            {currentIsVideo ? (
              <video
                key={resolvedUrl}
                src={resolvedUrl}
                className="max-w-[75vw] max-h-full object-contain rounded-xl shadow-2xl"
                controls
                autoPlay
                playsInline
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <>
                {imageStage === "thumb" && (
                  <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                    <Loader2 className="w-8 h-8 animate-spin text-white/60" />
                  </div>
                )}
                <img
                  ref={imgRef}
                  src={previewSrc}
                  alt={current.alt || "Preview"}
                  className={`max-w-[75vw] max-h-full object-contain rounded-xl shadow-2xl transition-[filter] duration-300 ${imageStage === "full" ? "" : imageStage === "medium" ? "blur-[1px]" : "blur-sm"}`}
                  onLoad={handleImageLoad}
                />
              </>
            )}
          </div>

          {images.length > 1 && (
            <button
              className={`p-3 rounded-full bg-white/20 text-white transition-all flex-shrink-0 ${hasNext ? "hover:bg-white/30" : "opacity-30 cursor-not-allowed"}`}
              onClick={(e) => { e.stopPropagation(); if (hasNext) onNavigate(currentIndex + 1); }}
              disabled={!hasNext}
            >
              <ChevronRight size={24} />
            </button>
          )}
        </div>

        {imageMeta && (imageMeta.w > 0 || imageMeta.size || imageMeta.type) && (
          <div className="mt-3 flex items-center gap-3 text-white/60 text-sm">
            {imageMeta.w > 0 && <span>{imageMeta.w} × {imageMeta.h}</span>}
            {imageMeta.size != null && (
              <>
                <span className="text-white/30">·</span>
                <span>{formatFileSize(imageMeta.size)}</span>
              </>
            )}
            {imageMeta.type && (
              <>
                <span className="text-white/30">·</span>
                <span>{imageMeta.type}</span>
              </>
            )}
          </div>
        )}

        {images.length > 1 && (
          <div className="mt-3 flex gap-2 overflow-x-auto max-w-[80vw] pb-2">
            {images.map((img, idx) => (
              <button
                key={idx}
                className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all shrink-0 ${idx === currentIndex ? "border-white shadow-lg scale-105" : "border-transparent opacity-60 hover:opacity-100"}`}
                onClick={(e) => { e.stopPropagation(); onNavigate(idx); }}
              >
                {Math.abs(idx - currentIndex) < 10 ? (
                  (img.isVideo === true || isVideoUrl(img.url)) ? (
                    <div className="relative w-full h-full bg-black flex items-center justify-center">
                      <video
                        src={getImageUrl(img.url)}
                        className="w-full h-full object-cover"
                        muted
                        playsInline
                        preload="metadata"
                      />
                      <Play className="absolute w-5 h-5 text-white fill-white pointer-events-none" />
                    </div>
                  ) : (
                    <img src={getThumbnailUrl(img.url, 128)} alt="" className="w-full h-full object-cover" loading="lazy" />
                  )
                ) : (
                  <div className="w-full h-full bg-white/10" />
                )}
              </button>
            ))}
          </div>
        )}

        <div className="mt-3 flex items-center justify-center gap-4 flex-shrink-0">
          {images.length > 1 && (
            <span className="text-white/60 text-sm">
              {currentIndex + 1} / {images.length}
            </span>
          )}
          {canSaveOutfit && (
            <button
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm transition-all ${
                isAlreadyOutfit
                  ? "bg-emerald-500/30 text-emerald-200 cursor-default"
                  : "bg-primary/80 text-white hover:bg-primary"
              } ${savingOutfit ? "opacity-60 cursor-wait" : ""}`}
              onClick={handleSaveOutfit}
              disabled={savingOutfit || isAlreadyOutfit}
              title={isAlreadyOutfit ? "Already saved to Outfit / Style" : "Save to the Outfit / Style filter in your Asset Library"}
            >
              {isAlreadyOutfit ? <Check size={14} /> : savingOutfit ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {isAlreadyOutfit ? "Saved to Outfit" : savingOutfit ? "Saving…" : "Save to Outfit / Style"}
            </button>
          )}
          <button
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-white/20 text-white text-sm hover:bg-white/30 transition-all"
            onClick={handleDownload}
            title="Download image"
          >
            <Download size={14} />
            Download
          </button>
          <button
            className="px-4 py-1.5 rounded-lg bg-white/20 text-white text-sm hover:bg-white/30 transition-all"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export function ClickableImage({
  src,
  alt,
  className,
  gallery,
  galleryIndex,
  isOutfit,
  onSaveToOutfit,
  onEdit,
  isVideo,
}: {
  src: string;
  alt?: string;
  className?: string;
  gallery?: LightboxImage[];
  galleryIndex?: number;
  isOutfit?: boolean;
  onSaveToOutfit?: () => Promise<void> | void;
  /**
   * When set, an Edit (pencil) button appears in the lightbox toolbar.
   * Same semantics as LightboxImage.onEdit.
   */
  onEdit?: () => void;
  /**
   * When true (or when `src` carries a video extension) the thumbnail is a
   * video clip: it renders a `<video>` poster frame with a play affordance
   * and opens the lightbox in video-playback mode.
   */
  isVideo?: boolean;
}) {
  const { open, openSingle } = useLightbox();
  const renderAsVideo = isVideo === true || isVideoUrl(src);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (gallery && galleryIndex !== undefined) {
      open(gallery, galleryIndex);
    } else if (
      onSaveToOutfit !== undefined ||
      isOutfit !== undefined ||
      onEdit !== undefined
    ) {
      open([{ url: src, alt, isOutfit, onSaveToOutfit, onEdit, isVideo: renderAsVideo }], 0);
    } else if (renderAsVideo) {
      open([{ url: src, alt, isVideo: true }], 0);
    } else {
      openSingle(src, alt);
    }
  };

  if (renderAsVideo) {
    return (
      <div
        className={`${className || ""} relative cursor-pointer`}
        onClick={handleClick}
      >
        <video
          src={getImageUrl(src)}
          className="w-full h-full object-cover"
          muted
          playsInline
          preload="metadata"
        />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="rounded-full bg-black/50 p-1.5">
            <Play className="w-4 h-4 text-white fill-white" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <img
      src={getThumbnailUrl(src, 300)}
      alt={alt || ""}
      className={`${className || ""} cursor-pointer`}
      onClick={handleClick}
      loading="lazy"
    />
  );
}
